import type { CacheSettings, FeatureApiResponse } from "@growthbook/growthbook";
import type { UnwrapNestedRefs } from "vue";

import { GrowthBook, helpers } from "@growthbook/growthbook";
import { computed, reactive, toRefs, watch } from "vue";

import type { ToMaybeRefs } from "@solvari/utils";

import {
  addDataToSentryProject,
  getEnv,
  isError,
  returnError,
} from "@solvari/utils";

import type { BrowserAttributes } from "@/client/browserAttributes.ts";
import type { PageAttributes, SiteAttributes } from "@/core/attributes.ts";

import { addAbTestEvent } from "@/client/analytics.ts";
import { getBrowserAttributes } from "@/client/browserAttributes.ts";
import { markAbTestingLoaded, whenAbTestingLoaded } from "@/client/loaded.ts";
import { setAbTestFeatureReader } from "@/client/reader.ts";
import { getSentryAbTesting } from "@/client/sentry.ts";

type AbTestingClientInstance = {
  growthBook: UnwrapNestedRefs<GrowthBook>;
  pageAttributes: PageAttributes;
};

const growthBookApiHost = "https://cdn.growthbook.io";

// SPAs must pass `browserAttributes` or targeting keeps matching the landing page.
function collectAttributes(
  solvariClientId: string,
  siteAttributes: ToMaybeRefs<SiteAttributes>,
  browserAttributes?: BrowserAttributes,
) {
  const pageAttributes: PageAttributes = reactive({
    formGroupId: null,
    formId: null,
  });

  const attributes = reactive({
    solvariClientId,
    ...siteAttributes,
    ...(browserAttributes ?? getBrowserAttributes()),
    ...toRefs(pageAttributes),
    hasForm: computed(
      () => !!pageAttributes.formId || !!pageAttributes.formGroupId,
    ),
  });

  return { attributes, pageAttributes };
}

// Constructs the instance only: no payload applied, no feature read.
function makeGrowthBook(
  attributes: Record<string, unknown>,
): UnwrapNestedRefs<GrowthBook> {
  const growthBook = reactive(
    new GrowthBook({
      apiHost: growthBookApiHost,
      clientKey: getEnv().integrations.growthbook.clientKey,
      attributes,
      trackingCallback: addAbTestEvent,
      enableDevMode: true,
      // GrowthBook's own redirect type would race our split-url watchers
      disableUrlRedirectExperiments: true,
    }),
  );

  watch(attributes, (attributes) => {
    void growthBook.setAttributes(attributes);
  });

  addDataToSentryProject("mkt-ab-testing", () => ({
    contexts: {
      attributes: growthBook.getAttributes(),
      features: growthBook.getFeatures() as Record<string, unknown>,
    },
  }));

  return growthBook;
}

// CDN init path (no render slice). Errors reported to Sentry; the caller still proceeds.
async function initFromCdn(
  growthBook: UnwrapNestedRefs<GrowthBook>,
): Promise<void> {
  const result = await returnError(() =>
    growthBook.init({
      streaming: false,
      // Partial is safe: `configureCache` Object.assigns it onto the defaults.
      cacheSettings: { disableCache: true } as CacheSettings,
    }),
  )();

  if (isError(result)) {
    getSentryAbTesting().captureException(result);
  } else if (result.error) {
    getSentryAbTesting().captureException(result.error);
  }
}

// Never awaits the network: the CDN merge lands later and resolves `whenAbTestingLoaded`.
async function createAbTestingClient({
  browserAttributes,
  clientId,
  payload,
  siteAttributes,
}: {
  browserAttributes?: BrowserAttributes;
  clientId: string;
  payload?: FeatureApiResponse;
  siteAttributes: ToMaybeRefs<SiteAttributes>;
}): Promise<AbTestingClientInstance> {
  const { attributes, pageAttributes } = collectAttributes(
    clientId,
    siteAttributes,
    browserAttributes,
  );

  const growthBook = makeGrowthBook(attributes);

  // Bind before any await: Vue hydrates from this instance even if the CDN fetch races the 1s window.
  setAbTestFeatureReader(growthBook);
  globalThis.abTestingAttributes = pageAttributes;
  void whenAbTestingLoaded();

  const instance: AbTestingClientInstance = { growthBook, pageAttributes };

  if (payload === undefined) {
    void initFromCdn(growthBook).finally(markAbTestingLoaded);
    return instance;
  }

  // setPayload applies savedGroups on every version; initSync only from 1.7.0.
  const applied = await returnError(() => growthBook.setPayload(payload))();
  if (isError(applied)) {
    getSentryAbTesting().captureException(applied);
  }

  void refreshPayload(instance, payload).finally(markAbTestingLoaded);

  return instance;
}

/**
 * Merges the fresh CDN payload with the embedded render slice: embedded render features and their
 * transitive prerequisite features win over the fresh payload for the page's lifetime — a render
 * feature removed or edited upstream stays pinned to the page's version until the next server
 * render. Saved groups are pinned instance-wide (the whole `savedGroups` map is merged with
 * embedded winning). Everything else (client-only features, experiments, tracking) comes from
 * `fresh`.
 */
function mergeRenderPayload(
  fresh: FeatureApiResponse,
  embedded: FeatureApiResponse,
): FeatureApiResponse {
  const merged: FeatureApiResponse = {
    ...fresh,
    features: { ...fresh.features, ...embedded.features },
  };

  if (fresh.savedGroups !== undefined || embedded.savedGroups !== undefined) {
    merged.savedGroups = { ...fresh.savedGroups, ...embedded.savedGroups };
  }

  return merged;
}

// Fetches the full CDN payload and merges the embedded render slice over it: render features
// stay pinned to the page's version; client-only features come from the fresh fetch.
async function refreshPayload(
  instance: AbTestingClientInstance,
  embedded: FeatureApiResponse,
): Promise<void> {
  const result = await returnError(async () => {
    const res = await helpers.fetchFeaturesCall({
      host: growthBookApiHost,
      clientKey: getEnv().integrations.growthbook.clientKey,
    });
    if (!res.ok) {
      throw new Error(`HTTP error: ${res.status}`);
    }
    const fresh = (await res.json()) as FeatureApiResponse;
    if (!fresh.features) {
      throw new Error("GrowthBook payload carries no features");
    }
    await instance.growthBook.setPayload(mergeRenderPayload(fresh, embedded));
  })();

  if (isError(result)) {
    getSentryAbTesting().captureException(result);
  }
}

export type { AbTestingClientInstance };
export { createAbTestingClient, mergeRenderPayload };
